// ecountSales.js — 단가마스터 앱(price-manager-49b39, 이카운트 API 연동)이 수집한 일별 매출을
// 읽기 전용으로 조회하는 공용 모듈. priceManager.js와 동일하게 2번째 Firebase 앱("priceManager")을 재사용한다.
//
// [데이터 계약] price-manager 프로젝트의 'salesDaily' 컬렉션 (문서 1개 = 업체·일자별 매출 집계)
//   date       : "YYYY-MM-DD"  (판매일)
//   vendorCode : 거래처코드 (이카운트 CUST 코드, 선택)
//   vendorName : 거래처명(업체명)
//   amount       : 매출액 합계 (원, 부가세 포함 = 공급가액 × 1.1) — 실제 입금 기준
//   supplyAmount : 매출액 합계 (원, 공급가액 = 부가세 제외) — 원가·마진 계산 기준
//   qty        : 수량 합계 (선택)
//
// [부가세 기준] 업로드 형식마다 엑셀 값이 부가세 포함인지 아닌지가 다르다. 저장은 언제나
// amount(부가세 포함) · supplyAmount(공급가액) 두 값을 함께 남긴다 — 그래야 매출총이익 계산이 어긋나지 않는다.
//   · 홈택스 전자세금계산서 목록 : '공급가액' 열 = 부가세 제외  → amount = 값 × 1.1
//   · 이카운트 판매현황          : 금액 열 = 부가세 포함(2026-09-02 사용자 확인)
//                                 → amount = 값 그대로, supplyAmount = 값 ÷ 1.1
// ⚠️ 2026-09-02 이전에는 판매현황도 ×1.1을 했다. 그때 올린 데이터는 매출이 10% 부풀려져 있으므로
//    같은 기간 판매현황을 다시 업로드해야 바로잡힌다(업로드는 같은 날짜·거래처 문서를 덮어쓴다).
// supplyAmount 필드가 없는 구버전 문서는 amount가 공급가액이므로 읽을 때 환산해준다.
// price-manager 쪽 수집 배치가 위 형태로 저장하면 재고 앱 '업체별 매출' 화면에 자동 표시된다.
import { initializeApp, getApp, getApps } from "firebase/app";
import { getAuth, signInAnonymously } from "firebase/auth";
import { getFirestore, collection, getDocs, query, where, orderBy, doc, writeBatch } from "firebase/firestore";

const PRICE_MANAGER_CONFIG = {
  apiKey: "AIzaSyDfoT7ImaiqGNuvOeBH6gaVUZFI6fEDnZs",
  authDomain: "price-manager-49b39.firebaseapp.com",
  projectId: "price-manager-49b39",
};

// 매입세금계산서 모듈(purchaseTaxInvoice.js)도 동일한 price-manager Firebase 앱을 재사용하도록 export.
export function priceManagerApp() {
  return getApps().find((a) => a.name === "priceManager")
    ? getApp("priceManager")
    : initializeApp(PRICE_MANAGER_CONFIG, "priceManager");
}

function db() {
  return getFirestore(priceManagerApp());
}

let signInPromise = null;
export function ensureSignedIn() {
  const auth = getAuth(priceManagerApp());
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!signInPromise) {
    signInPromise = signInAnonymously(auth)
      .then((cred) => cred.user)
      .catch((e) => { signInPromise = null; throw e; });
  }
  return signInPromise;
}

const ymd = (d) => d.toISOString().slice(0, 10);

// 부가세율 — 매출 금액을 "실제 입금 기준(부가세 포함)"과 "공급가액" 사이에서 환산할 때 쓴다.
export const VAT_RATE = 1.1;
export const withVat = (supply) => Math.round(supply * VAT_RATE);      // 공급가액 → 부가세 포함
export const withoutVat = (total) => Math.round(total / VAT_RATE);     // 부가세 포함 → 공급가액

// 기간 선택값 → 조회 범위 {from, to}("YYYY-MM-DD", null이면 제한 없음).
// 지원 값
//   숫자 N  : 최근 N일   |  0 : 전체   |  "ytd" : 올해 1월 1일부터
//   "q1"~"q4" : 올해 분기  |  "h1"·"h2" : 올해 상·하반기
// ⚠️ 분기·반기는 언제나 '올해' 기준이다. 작년 분기를 보려면 연도 선택 UI를 따로 붙여야 한다.
// 매출(salesDaily)·매입(purchaseDaily) 두 조회가 같은 규칙을 쓰도록 여기 한 곳에만 둔다.
export function rangeBounds(range) {
  const y = new Date().getFullYear();
  const p2 = (n) => String(n).padStart(2, "0");
  const lastDay = (mm) => new Date(y, mm, 0).getDate(); // mm=1~12의 말일
  const span = (sm, em) => ({ from: `${y}-${p2(sm)}-01`, to: `${y}-${p2(em)}-${p2(lastDay(em))}` });
  switch (range) {
    case "ytd": return { from: `${y}-01-01`, to: null };
    case "q1": return span(1, 3);
    case "q2": return span(4, 6);
    case "q3": return span(7, 9);
    case "q4": return span(10, 12);
    case "h1": return span(1, 6);
    case "h2": return span(7, 12);
    default: break;
  }
  const n = Number(range);
  if (Number.isFinite(n) && n > 0) {
    const start = new Date();
    start.setDate(start.getDate() - n + 1);
    return { from: ymd(start), to: null };
  }
  return { from: null, to: null }; // 전체
}

// 선택 기간(rangeBounds 참고)의 매출 레코드를 반환.
export async function fetchSalesRecords(days = 30) {
  await ensureSignedIn();
  const col = collection(db(), "salesDaily");
  const { from, to } = rangeBounds(days);
  const conds = [];
  if (from) conds.push(where("date", ">=", from));
  if (to) conds.push(where("date", "<=", to));
  const q = query(col, ...conds, orderBy("date", "desc"));
  const snap = await getDocs(q);
  const out = [];
  snap.docs.forEach((d) => {
    const x = d.data();
    const date = String(x.date || "").slice(0, 10);
    const stored = Number(x.amount);
    if (!date || !Number.isFinite(stored)) return;
    // supplyAmount가 있으면 신버전 문서(amount = 부가세 포함).
    // 없으면 구버전 문서라 amount가 공급가액이므로 읽는 시점에 부가세를 붙여 기준을 통일한다.
    const savedSupply = Number(x.supplyAmount);
    const hasSupply = Number.isFinite(savedSupply);
    out.push({
      date,
      vendorCode: String(x.vendorCode || "").trim(),
      vendorName: String(x.vendorName || "").trim() || "(업체 미지정)",
      amount: hasSupply ? stored : withVat(stored),
      supplyAmount: hasSupply ? savedSupply : stored,
      qty: Number(x.qty) || 0,
    });
  });
  return out;
}

// ─────────────────────────────────────────────────────────────
// 이카운트 '판매현황' 엑셀 업로드 → 업체·일자별 집계 → salesDaily 저장
// (이카운트 OpenAPI에 판매 "조회" API가 없어 엑셀 방식으로 수집. 2026-07-18 확정)
// ─────────────────────────────────────────────────────────────

const norm = (s) => String(s ?? "").replace(/\s/g, "");

// 시트(배열의 배열)를 받아 [{date, vendorCode, vendorName, amount, qty}]로 파싱.
// 지원 형식 ① 전자(세금)계산서 목록(ETAX102M: 구분/공급받는자상호/공급가액 — '매출'만 집계, 수정계산서 음수 반영)
//                → '공급가액' 열은 부가세 제외. amount에 ×1.1을 붙인다.
//          ② 이카운트 판매현황(일자/거래처/금액)
//                → 금액 열은 부가세 포함(2026-09-02 사용자 확인). 값을 그대로 amount로 쓰고 ÷1.1로 공급가액을 뽑는다.
export function parseSalesExcelRows(rows) {
  // ── 형식 ①: 전자세금계산서 목록 ──
  for (let i = 0; i < Math.min(rows.length, 12); i++) {
    const h = (rows[i] || []).map(norm);
    const gi = h.findIndex((c) => c === "구분");
    const vi = h.findIndex((c) => c.includes("공급받는자상호"));
    const ai = h.findIndex((c) => c === "공급가액");
    const di = h.findIndex((c) => c === "일자" || c === "작성일자");
    if (gi >= 0 && vi >= 0 && ai >= 0 && di >= 0) {
      const ci = h.findIndex((c) => c.includes("공급받는자사업자번호"));
      const records = [];
      for (let r = i + 1; r < rows.length; r++) {
        const row = rows[r] || [];
        if (String(row[gi] ?? "").trim() !== "매출") continue; // 매입·빈 행 제외
        const dateDigits = String(row[di] ?? "").replace(/[^0-9]/g, "").slice(0, 8);
        if (dateDigits.length !== 8) continue;
        const date = `${dateDigits.slice(0, 4)}-${dateDigits.slice(4, 6)}-${dateDigits.slice(6, 8)}`;
        const vendorName = String(row[vi] ?? "").trim();
        const supply = Number(String(row[ai] ?? "").replace(/,/g, "")); // 엑셀 값은 공급가액
        if (!vendorName || !Number.isFinite(supply) || supply === 0) continue;
        records.push({ date, vendorCode: String(ci >= 0 ? row[ci] ?? "" : "").trim(), vendorName, amount: withVat(supply), supplyAmount: supply, qty: 0 });
      }
      return { error: records.length ? "" : "'매출' 구분의 계산서 행을 찾지 못했습니다.", records };
    }
  }

  // ── 형식 ②: 이카운트 판매현황 (일별/일자 + 거래처별/거래처 + 공급가액) ──
  let hi = -1, idx = {};
  for (let i = 0; i < Math.min(rows.length, 12); i++) {
    const h = (rows[i] || []).map(norm);
    const di = h.findIndex((c) => c.includes("일자") || c.includes("일별"));
    const vi = h.findIndex((c) => c.includes("거래처"));
    const ai = h.findIndex((c) => c.includes("공급가액") || c.includes("판매금액") || c === "금액" || c.includes("합계"));
    if (di >= 0 && vi >= 0 && ai >= 0) {
      hi = i;
      idx = {
        date: di,
        vendorCode: h.findIndex((c) => c.includes("거래처코드")),
        vendorName: (() => { const n = h.findIndex((c) => c.includes("거래처명")); return n >= 0 ? n : vi; })(),
        amount: (() => { const s = h.findIndex((c) => c.includes("공급가액")); return s >= 0 ? s : ai; })(),
        qty: h.findIndex((c) => c === "수량" || c.includes("판매수량")),
      };
      break;
    }
  }
  if (hi < 0) return { error: "헤더 행을 찾지 못했습니다. '일자(또는 일별)·거래처·공급가액(또는 금액)' 열이 포함된 판매현황 엑셀인지 확인해주세요.", records: [] };

  const records = [];
  let lastDate = "", lastVendorName = "", lastVendorCode = "";
  for (let i = hi + 1; i < rows.length; i++) {
    const r = rows[i] || [];
    const dateRaw = String(idx.date >= 0 ? r[idx.date] ?? "" : "").trim();
    const nameRaw = String(idx.vendorName >= 0 ? r[idx.vendorName] ?? "" : "").trim();
    // 거래처별/일자별 소계·총합계 행 제외 (예: 일별 열에 "OOO 계"/"2026/06/30 계"/"총합계"가 찍히는 형식 —
    // 이 행들을 걸러내지 않으면 매출이 중복 집계되거나 마지막 거래처에 엉뚱한 합계 금액이 붙는다).
    if (/합계|소계/.test(nameRaw) || /\s계\s*$/.test(nameRaw) || /총합계/.test(dateRaw) || /\s계\s*$/.test(dateRaw)) continue;
    const dateDigits = dateRaw.replace(/[^0-9]/g, "").slice(0, 8);
    const date = dateDigits.length === 8
      ? `${dateDigits.slice(0, 4)}-${dateDigits.slice(4, 6)}-${dateDigits.slice(6, 8)}`
      : lastDate; // 병합 셀 대비: 비어 있으면 직전 일자 사용
    if (!date) continue;
    const vendorName = nameRaw || lastVendorName;
    const vendorCode = String(idx.vendorCode >= 0 ? r[idx.vendorCode] ?? "" : "").trim() || (nameRaw ? "" : lastVendorCode);
    // 판매현황 엑셀의 금액은 부가세가 포함된 총액이다 — 여기에 다시 1.1을 곱하면 이중 계산이 된다.
    const total = Number(String(r[idx.amount] ?? "").replace(/,/g, ""));
    if (!vendorName || !Number.isFinite(total) || total === 0) {
      if (dateDigits.length === 8) lastDate = date;
      if (nameRaw) { lastVendorName = nameRaw; lastVendorCode = String(idx.vendorCode >= 0 ? r[idx.vendorCode] ?? "" : "").trim(); }
      continue;
    }
    const qty = Number(String(idx.qty >= 0 ? r[idx.qty] ?? "" : "").replace(/,/g, "")) || 0;
    records.push({ date, vendorCode, vendorName, amount: total, supplyAmount: withoutVat(total), qty });
    lastDate = date;
    if (nameRaw) { lastVendorName = nameRaw; lastVendorCode = vendorCode; }
  }
  return { error: records.length ? "" : "인식된 매출 행이 없습니다.", records };
}

function aggregateSalesRecords(records) {
  const map = {};
  records.forEach((r) => {
    const key = `${r.date}_${r.vendorCode || r.vendorName}`;
    if (!map[key]) map[key] = { date: r.date, vendorCode: r.vendorCode, vendorName: r.vendorName, amount: 0, supplyAmount: 0, qty: 0, lines: 0 };
    // 두 값 모두 건별로 반올림한 뒤 더한다 — 합계를 한 번에 환산하는 방식과
    // 최대 몇 원 차이가 날 수 있지만, 건별 세금계산서 금액과 맞추는 쪽을 기준으로 삼는다.
    map[key].amount += r.amount;
    map[key].supplyAmount += (r.supplyAmount != null ? r.supplyAmount : r.amount);
    map[key].qty += r.qty;
    map[key].lines += 1;
  });
  return Object.values(map);
}

async function writeAggregatedSales(database, aggregated) {
  let batch = writeBatch(database);
  let n = 0;
  for (const a of aggregated) {
    const id = `${a.date}_${(a.vendorCode || a.vendorName).replace(/[/\s#?%[\]]/g, "_")}`;
    batch.set(doc(database, "salesDaily", id), { ...a, updatedAt: new Date(), source: "excel" }, { merge: true });
    n += 1;
    if (n % 400 === 0) { await batch.commit(); batch = writeBatch(database); }
  }
  if (n % 400 !== 0) await batch.commit();
}

// 파싱된 레코드를 업체·일자별로 합산해 salesDaily에 저장(같은 업체·일자는 덮어씀, 그 외 기존 데이터는 그대로 유지 → 재업로드 안전).
export async function saveSalesRecords(records) {
  await ensureSignedIn();
  const aggregated = aggregateSalesRecords(records);
  await writeAggregatedSales(db(), aggregated);
  const dates = aggregated.map((a) => a.date).sort();
  return { saved: aggregated.length, vendors: new Set(aggregated.map((a) => a.vendorName)).size, from: dates[0], to: dates[dates.length - 1] };
}

// salesDaily 컬렉션을 통째로 비운 뒤 이번에 파싱한 레코드로만 다시 채운다("매출 자료 전체 교체").
// ⚠️ saveSalesRecords와 달리 병합이 아니라 완전 교체다 — 지금 올리는 파일에 없는 기간/거래처의
// 기존 매출 데이터는 전부 삭제된다. 특정 기간(예: 이번 달)만 담긴 파일을 이 모드로 올리면
// 그 기간 밖의 매출 이력이 사라지므로, 반드시 "현재까지의 전체 매출" 파일일 때만 사용할 것.
export async function replaceSalesRecords(records) {
  await ensureSignedIn();
  const database = db();
  const existing = await getDocs(collection(database, "salesDaily"));
  const deleteChunks = [];
  {
    let chunk = writeBatch(database);
    let c = 0;
    existing.docs.forEach((d) => {
      chunk.delete(d.ref);
      c += 1;
      if (c === 400) { deleteChunks.push(chunk); chunk = writeBatch(database); c = 0; }
    });
    if (c > 0) deleteChunks.push(chunk);
  }
  for (const b of deleteChunks) await b.commit();

  const aggregated = aggregateSalesRecords(records);
  await writeAggregatedSales(database, aggregated);
  const dates = aggregated.map((a) => a.date).sort();
  return { deleted: existing.docs.length, saved: aggregated.length, vendors: new Set(aggregated.map((a) => a.vendorName)).size, from: dates[0], to: dates[dates.length - 1] };
}

// 업체별 집계: [{vendor, code, total, qty, dayCount, lastDate, daily:{date:amount}}] (매출 내림차순)
export function summarizeByVendor(records) {
  const map = {};
  (records || []).forEach((r) => {
    const key = r.vendorCode || r.vendorName;
    if (!map[key]) map[key] = { vendor: r.vendorName, code: r.vendorCode, total: 0, qty: 0, daily: {}, lastDate: "" };
    const v = map[key];
    v.total += r.amount;
    v.qty += r.qty;
    v.daily[r.date] = (v.daily[r.date] || 0) + r.amount;
    if (r.date > v.lastDate) v.lastDate = r.date;
  });
  return Object.values(map)
    .map((v) => ({ ...v, dayCount: Object.keys(v.daily).length }))
    .sort((a, b) => b.total - a.total);
}
